import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { CreditCard, Check, X, RefreshCw, Trash2, Shield, ExternalLink, Loader2, AlertCircle, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SellerMercadoPagoConfig = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    access_token: '',
    public_key: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/seller/mercadopago/config`, { 
        withCredentials: true 
      });
      setConfig(response.data);
      setShowForm(!response.data.configured);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.access_token || !formData.public_key) {
      toast.error('Preencha todos os campos');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/seller/mercadopago/config`,
        formData,
        { withCredentials: true }
      );
      toast.success('Configuração salva com sucesso!');
      setShowForm(false);
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await axios.post(
        `${API_URL}/seller/mercadopago/test`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success('Conexão bem sucedida!');
        fetchConfig();
      } else {
        toast.error(response.data.message || 'Erro na conexão');
      }
    } catch (error) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja remover a configuração do Mercado Pago?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/seller/mercadopago/config`, { 
        withCredentials: true 
      });
      toast.success('Configuração removida');
      setConfig(null);
      setShowForm(true);
      setFormData({ access_token: '', public_key: '' });
    } catch (error) {
      toast.error('Erro ao remover configuração');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
            <Check className="w-4 h-4" />
            Ativo
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
            <X className="w-4 h-4" />
            Erro
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
            <AlertCircle className="w-4 h-4" />
            Pendente
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="mp-config-title">
                Mercado Pago
              </h1>
              <p className="text-gray-600">Receba pagamentos diretamente na sua conta</p>
            </div>
          </div>
        </div>

        {/* Status Card */}
        {config?.configured && !showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Status da Integração</h2>
              {getStatusBadge(config.status)}
            </div>

            <div className="space-y-3">
              {config.mp_email && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Email Mercado Pago</span>
                  <span className="font-medium text-gray-900">{config.mp_email}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Chave Pública</span>
                <span className="font-mono text-sm text-gray-900">
                  {config.public_key?.substring(0, 20)}...
                </span>
              </div>

              {config.last_tested && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Último teste</span>
                  <span className="text-gray-900">
                    {new Date(config.last_tested).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleTest}
                disabled={testing}
                variant="outline"
                className="flex-1"
                data-testid="test-connection-btn"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Testar Conexão
              </Button>
              
              <Button
                onClick={() => setShowForm(true)}
                variant="outline"
                className="flex-1"
              >
                Atualizar Credenciais
              </Button>
              
              <Button
                onClick={handleDelete}
                variant="outline"
                className="text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Configurar Credenciais
            </h2>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label htmlFor="access_token">Access Token</Label>
                <Input
                  id="access_token"
                  type="password"
                  value={formData.access_token}
                  onChange={(e) => setFormData({...formData, access_token: e.target.value})}
                  placeholder="APP_USR-..."
                  className="mt-1 font-mono"
                  data-testid="access-token-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Token de acesso para processar pagamentos
                </p>
              </div>

              <div>
                <Label htmlFor="public_key">Public Key</Label>
                <Input
                  id="public_key"
                  type="text"
                  value={formData.public_key}
                  onChange={(e) => setFormData({...formData, public_key: e.target.value})}
                  placeholder="APP_USR-..."
                  className="mt-1 font-mono"
                  data-testid="public-key-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Chave pública para o checkout do cliente
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500"
                  data-testid="save-config-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Salvar Configuração
                </Button>
                
                {config?.configured && (
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Help Section */}
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">
                Como obter suas credenciais?
              </h3>
              <ol className="text-blue-800 text-sm space-y-2">
                <li>1. Acesse sua conta no Mercado Pago</li>
                <li>2. Vá em "Seu negócio" → "Configurações" → "Credenciais"</li>
                <li>3. Escolha "Credenciais de produção" ou "Credenciais de teste"</li>
                <li>4. Copie o Access Token e Public Key</li>
              </ol>
              
              <a
                href="https://www.mercadopago.com.br/developers/panel/credentials"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium mt-3"
              >
                Acessar painel de credenciais
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-6 flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
          <Shield className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-600">
            Suas credenciais são armazenadas de forma segura e criptografada. 
            Nunca compartilhamos com terceiros.
          </p>
        </div>
      </div>
    </div>
  );
};
